/**
 * 二维码管理 API 服务
 * 提供二维码页面相关的所有API调用方法
 */
import api, { apiRequest } from '@/utils/api'

const QR_CODE_API_BASE = '/api/qr-codes'

/**
 * 二维码页面相关API
 */
export const qrCodeService = {
  /**
   * 获取二维码页面列表
   */
  async getPages(params = {}) {
    try {
      const pageSize = params.page_size || 20
      const response = await apiRequest.get(`${QR_CODE_API_BASE}/pages/`, params)
      const data = response.data
      const results = data.results || (Array.isArray(data) ? data : [])
      const count = data.count ?? results.length
      return {
        success: true,
        data,
        results,
        count,
        totalPages: Math.max(1, Math.ceil(count / pageSize)),
        page: params.page || 1,
      }
    } catch (error) {
      console.error('获取二维码页面列表失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取二维码页面列表失败'
      }
    }
  },

  /**
   * 获取二维码页面详情
   */
  async getPage(id) {
    try {
      const response = await apiRequest.get(`${QR_CODE_API_BASE}/pages/${id}/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取二维码页面详情失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取二维码页面详情失败'
      }
    }
  },

  /**
   * 创建二维码页面
   */
  async createPage(pageData) {
    try {
      const response = await apiRequest.post(`${QR_CODE_API_BASE}/pages/`, pageData)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('创建二维码页面失败:', error)
      
      // 🔧 优先提取detail字段的错误信息（配额限制等）
      const errorData = error.response?.data || {}
      let errorMessage = '创建二维码页面失败'
      
      if (errorData.detail) {
        errorMessage = errorData.detail
      } else if (errorData.message) {
        errorMessage = errorData.message
      } else if (errorData.error) {
        errorMessage = errorData.error
      }
      
      return {
        success: false,
        message: errorMessage,
        fieldErrors: errorData.errors || errorData || {}
      }
    }
  },

  /**
   * 更新二维码页面
   */
  async updatePage(id, pageData) {
    try {
      const response = await apiRequest.put(`${QR_CODE_API_BASE}/pages/${id}/`, pageData)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('更新二维码页面失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '更新二维码页面失败',
        fieldErrors: error.response?.data?.errors || error.response?.data || {}
      }
    }
  },

  /**
   * 删除二维码页面
   */
  async deletePage(id) {
    try {
      await apiRequest.delete(`${QR_CODE_API_BASE}/pages/${id}/`)
      return {
        success: true,
        message: '二维码页面删除成功'
      }
    } catch (error) {
      console.error('删除二维码页面失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '删除二维码页面失败'
      }
    }
  },

  /**
   * 复制二维码页面
   */
  async duplicatePage(id) {
    try {
      const response = await apiRequest.post(`${QR_CODE_API_BASE}/pages/${id}/duplicate/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('复制二维码页面失败:', error)
      
      // 🔧 优先提取error字段的错误信息（配额限制等）
      const errorData = error.response?.data || {}
      let errorMessage = '复制二维码页面失败'
      
      if (errorData.error) {
        errorMessage = errorData.error
      } else if (errorData.detail) {
        errorMessage = errorData.detail
      } else if (errorData.message) {
        errorMessage = errorData.message
      }
      
      return {
        success: false,
        message: errorMessage
      }
    }
  },

  /**
   * 生成HTML内容
   */
  async generateHtml(id) {
    try {
      const response = await apiRequest.post(`${QR_CODE_API_BASE}/pages/${id}/generate_html/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('生成HTML失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.error || '生成HTML失败'
      }
    }
  },

  /**
   * 检查域名是否可用
   */
  async checkDomain(id, domain) {
    try {
      const response = await apiRequest.post(`${QR_CODE_API_BASE}/pages/${id}/check_domain/`, { domain })
      return {
        success: response.data.success || true,
        available: response.data.available,
        message: response.data.message
      }
    } catch (error) {
      console.error('检查域名失败:', error)
      return {
        success: false,
        available: false,
        message: error.response?.data?.message || '检查域名失败'
      }
    }
  },

  /**
   * 验证自定义域名
   */
  async verifyDomain(id) {
    try {
      const response = await apiRequest.post(`${QR_CODE_API_BASE}/pages/${id}/verify_domain/`)
      return {
        success: response.data.success || true,
        message: response.data.message,
        data: response.data
      }
    } catch (error) {
      console.error('验证域名失败:', error)
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || '验证域名失败'
      }
    }
  },

  /**
   * 获取域名状态
   */
  async getDomainStatus(id) {
    try {
      const response = await apiRequest.get(`${QR_CODE_API_BASE}/pages/${id}/domain_status/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取域名状态失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取域名状态失败'
      }
    }
  },

  /**
   * 申请 SSL 证书
   */
  async applySSL(id) {
    try {
      const response = await apiRequest.post(`${QR_CODE_API_BASE}/pages/${id}/apply_ssl/`)
      return {
        success: response.data.success || true,
        message: response.data.message,
        data: response.data
      }
    } catch (error) {
      console.error('申请SSL失败:', error)
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || '申请SSL失败'
      }
    }
  },

  /**
   * 一键配置域名和 SSL
   */
  async verifyAndSetup(id) {
    try {
      const response = await apiRequest.post(`${QR_CODE_API_BASE}/pages/${id}/verify_and_setup/`)
      return {
        success: response.data.success || true,
        message: response.data.message,
        data: response.data
      }
    } catch (error) {
      console.error('一键配置失败:', error)
      return {
        success: false,
        message: error.response?.data?.error || error.response?.data?.message || '一键配置失败'
      }
    }
  },

  /**
   * 获取页面访问记录
   */
  async getVisits(id, page = 1, pageSize = 20) {
    try {
      const response = await apiRequest.get(`${QR_CODE_API_BASE}/pages/${id}/visits/`, {
        page,
        page_size: pageSize
      })
      return {
        success: true,
        data: response.data.data || [],
        total: response.data.total || 0,
        mobile_count: response.data.mobile_count || 0,
        desktop_count: response.data.desktop_count || 0,
        page: response.data.page || page,
        page_size: response.data.page_size || pageSize,
        total_pages: response.data.total_pages || 1
      }
    } catch (error) {
      console.error('获取访问记录失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取访问记录失败',
        data: [],
        total: 0
      }
    }
  },

  /**
   * 获取表单提交记录
   */
  async getSubmissions(id, page = 1, pageSize = 20) {
    try {
      const response = await apiRequest.get(`${QR_CODE_API_BASE}/pages/${id}/submissions/`, {
        page,
        page_size: pageSize
      })
      return {
        success: true,
        data: response.data.data || [],
        total: response.data.total || 0,
        mobile_count: response.data.mobile_count || 0,
        desktop_count: response.data.desktop_count || 0,
        processed_count: response.data.processed_count || 0,
        page: response.data.page || page,
        page_size: response.data.page_size || pageSize,
        total_pages: response.data.total_pages || 1
      }
    } catch (error) {
      console.error('获取表单提交记录失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取表单提交记录失败',
        data: [],
        total: 0
      }
    }
  }
}

/**
 * 二维码模板相关API
 */
export const qrTemplateService = {
  /**
   * 获取模板列表
   */
  async getTemplates(params = {}) {
    try {
      const response = await apiRequest.get(`${QR_CODE_API_BASE}/templates/`, params)
      return {
        success: true,
        data: response.data,
        results: response.data.results || response.data
      }
    } catch (error) {
      console.error('获取二维码模板列表失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取二维码模板列表失败'
      }
    }
  },

  /**
   * 获取模板详情
   */
  async getTemplate(id) {
    try {
      const response = await apiRequest.get(`${QR_CODE_API_BASE}/templates/${id}/`)
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      console.error('获取二维码模板详情失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取二维码模板详情失败'
      }
    }
  }
}

/**
 * 二维码分类相关API
 */
export const qrCategoryService = {
  /**
   * 获取分类列表
   */
  async getCategories(params = {}) {
    try {
      const response = await apiRequest.get(`${QR_CODE_API_BASE}/categories/`, params)
      return {
        success: true,
        data: response.data,
        results: response.data.results || response.data
      }
    } catch (error) {
      console.error('获取二维码分类列表失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取二维码分类列表失败'
      }
    }
  }
}

/**
 * 产品相关API (用于二维码页面)
 */
export const qrProductService = {
  /**
   * 获取产品列表
   */
  async getProducts(params = {}) {
    try {
      const response = await apiRequest.get('/api/products/v2/products/', params)
      return {
        success: true,
        data: response.data,
        results: response.data.results || response.data
      }
    } catch (error) {
      console.error('获取产品列表失败:', error)
      return {
        success: false,
        message: error.response?.data?.message || '获取产品列表失败'
      }
    }
  }
}

/**
 * 导出所有服务
 */
export default {
  qrCodeService,
  qrTemplateService,
  qrCategoryService,
  qrProductService
}
